/**
 * @title Should Load
 * @description Determines whether or not the Plyr element should be loaded.
 * @param {Element} target - The target element to be observed
 */
function shouldLoad(target) {
	/* The Intersection Observer's configuration object */
	const configuration = {
		rootMargin: "50% 0%",
	};

	return new Promise((resolve) => {
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				/* Disconnect the observer once it's been triggered */
				observer.disconnect();

				resolve(true);
			}
		}, configuration);

		observer.observe(target);
	});
}

/**
 * @title Should Pause
 * @description Determines whether or not the Plyr element should be paused.
 * @param {Element} target - The target element to be observed
 */
function shouldPause(target) {
	return new Promise((resolve) => {
		const observer = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) {
				/* Disconnect the observer once it's been triggered */
				observer.disconnect();

				resolve(true);
			}
		});

		observer.observe(target);
	});
}

/**
 * @title "ready" Callback
 * @description Logic to run when Plyr's "ready" event has been fired.
 * @param {Event} event - The listener's event
 */
function readyCallback(event) {
	console.log("readyCallback");
	const player = event.detail.plyr;
	const poster = player.elements.poster;
	const autoplay = player.config.autoplay;

	if (poster && !autoplay) poster.classList.add("opacity-0");

	/* FIX: Required to make the autoplay feature work in some browsers */
	player.muted = player.config.autoplay ? true : false;
}

/**
 * @title "progress" Callback
 * @description Logic to run when Plyr's "progress" event has been fired.
 * @param {Event} event - The listener's event
 */
function progressCallback(event) {
	const player = event.detail.plyr;
	const poster = player.elements.poster;

	if (poster) poster.classList.add("opacity-0");
}

/**
 * @title "restart" Callback
 * @description Logic to pause/resume playback when the entry is within the viewport.
 * @param {Event} event - The listener's event
 */
function restartCallback(player) {
	const autoplay = player.config.autoplay;

	if (autoplay) {
		let observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting && !player.paused) {
						player.pause();
					} else if (player.paused) {
						player.play();
					}
				});
			},
			{ threshold: 0.2 }
		);
		observer.observe(player.elements.container);
	}
}

/**
 * @title "play" Callback
 * @description Logic to run when Plyr's "play" event has been fired.
 * @param {Event} event - The listener's event
 */
function playCallback(event) {
	const player = event.detail.plyr;
	const autoplay = player.config.autoplay;

	/* This nested function allows async/await compatibility with Plyr.
	At the time of writing, Plyr's `isFunction()` method returns `false` for async functions. */
	if (!autoplay) {
		(async () => {
			const shouldPauseResult = await shouldPause(player.elements.container);
			if (shouldPauseResult) player.pause();
		})();
	}
}

export async function initPlyr(element) {
	const container = element.querySelector("[data-plyr-container]");
	const autoplay = element.hasAttribute(["data-plyr-autoplay"]);
	const poster = element.querySelector("[data-plyr-poster]");

	/* Options to use for autoplaying videos */
	const autoplayOptions = {
		autoplay: true,
		muted: true,
		controls: false,
		clickToPlay: false,
		fullscreen: false,
		loop: { active: true },
		vimeo: {
			muted: true,
			background: 1,
		},
	};

	/* Options to use for all videos */
	// const options = {
	// 	...(autoplay && autoplayOptions),
	// 	vimeo: {
	// 		dnt: true,
	// 		...(autoplay && autoplayOptions.vimeo),
	// 	},
	// };
	/* Options to use for all videos */
	const options = {
		vimeo: {
			dnt: true,
		},
	};

	if (autoplay) {
		Object.assign(options, autoplayOptions);
		Object.assign(options.vimeo, autoplayOptions.vimeo);
	}

	Object.assign(options.vimeo, { dnt: true });

	const shouldLoadResult = await shouldLoad(element);
	const player = new Plyr(container, options);

	/* Replace the default Plyr poster element with the custom one */
	player.elements.poster = poster;

	/* When the player is ready... */
	element.addEventListener("ready", readyCallback);

	/* When the player has buffered and is ready to play... */
	element.addEventListener("progress", progressCallback, { once: true });

	/* When the player has started playing... */
	element.addEventListener("play", playCallback);

	/* Watch video for viewport */
	restartCallback(player);
}
